import { useEffect, useState } from "react";
import { ExternalLink, Lock, LockOpen } from "lucide-react";
import { fmtNum } from "../hooks/usePoll";
import Reveal from "./Reveal";

const pad = (n) => String(n).padStart(2, "0");

function useCountdown(unlockAt) {
    const [now, setNow] = useState(() => Date.now() / 1000);
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now() / 1000), 1000);
        return () => clearInterval(t);
    }, []);
    if (!unlockAt) return null;
    const s = Math.max(0, Math.floor(unlockAt - now));
    return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60, done: s === 0 };
}

export default function TeamLock({ lock }) {
    const cd = useCountdown(lock?.unlockAt);
    const pct = lock ? Math.round(lock.pctOfSupply) : 26;
    const unlockDate = lock
        ? new Date(lock.unlockAt * 1000).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })
        : null;
    const locked = lock ? lock.active && !cd?.done : true;

    return (
        <Reveal delay={0.1} className="mt-6">
            <div
                data-testid="team-lock-card"
                className="glass relative overflow-hidden rounded-2xl border-purple-400/25 p-6 md:p-8"
            >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_80%_at_0%_50%,rgba(153,69,255,0.14),transparent)]" />
                <div className="relative grid items-center gap-8 lg:grid-cols-[auto_1fr_auto]">
                    <div className="flex items-center gap-4">
                        <span className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${locked ? "border-purple-400/40 bg-purple-500/15 text-purple-300" : "border-white/15 bg-white/5 text-slate-300"}`}>
                            {locked ? <Lock className="h-7 w-7" /> : <LockOpen className="h-7 w-7" />}
                        </span>
                        <div>
                            <span className="kicker !text-purple-300">TEAM TOKENS {locked ? "LOCKED" : "UNLOCKED"}</span>
                            <div data-testid="team-lock-pct" className="headline mt-1 text-4xl text-white md:text-5xl">
                                {pct}% <span className="text-2xl text-slate-400 md:text-3xl">OF SUPPLY</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                        <div>
                            <div className="kicker !text-[10px]">AMOUNT</div>
                            <div data-testid="team-lock-amount" className="mt-1 font-mono text-sm font-bold text-white md:text-base">
                                {lock ? fmtNum(lock.locked) : "—"} <span className="text-slate-500">$BALD</span>
                            </div>
                        </div>
                        <div>
                            <div className="kicker !text-[10px]">UNLOCKS</div>
                            <div data-testid="team-lock-date" className="mt-1 font-mono text-sm font-bold text-white md:text-base">{unlockDate || "—"}</div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <div className="kicker !text-[10px]">COUNTDOWN</div>
                            <div data-testid="team-lock-countdown" className="mt-1 font-mono text-sm font-bold tabular-nums text-purple-200 md:text-base">
                                {cd ? (cd.done ? "UNLOCKED" : `${cd.d}D ${pad(cd.h)}H ${pad(cd.m)}M ${pad(cd.s)}S`) : "—"}
                            </div>
                        </div>
                    </div>

                    <a
                        href={lock?.url || "https://app.streamflow.finance/contract/solana/mainnet/2MatmnKmjNURe4CCEoWV5E4TZbX62kCdNHPnZg4g6p5S"}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="team-lock-streamflow-button"
                        className="btn-ghost !border-purple-400/40 !text-purple-200 hover:!border-purple-300"
                    >
                        VERIFY ON STREAMFLOW <ExternalLink className="h-4 w-4" />
                    </a>
                </div>
                <p className="relative mt-5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    Locked in a Streamflow vesting contract on Solana. Balance, unlock date and status are read live from the contract account.
                </p>
            </div>
        </Reveal>
    );
}
